import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "@/services/api/apiService";
import {
  FRONTEND_CONFIG_QUERY_KEY,
  FRONTEND_CONFIG_STALE_MS,
  PLATFORM_QUERY_DEFAULTS,
  readActiveTenantQueryScope,
  tenantScopedQueryKey,
  useActiveTenantQueryScope,
} from "@/lib/hooks/query-options";
import { getPlatformQueryClient } from "@/lib/providers/query-provider";
import { gatewayResponseErrorMessage } from "@/lib/utils/response";

interface NavigationItem {
  id: string;
  label: string;
  labelKey?: string;
  icon: string;
  path: string;
  section?: string;
  parentPath?: string;
  requiredPermissions?: string[];
  hrmSubgroup?: string;
  navModule?: string;
  children?: NavigationItem[];
}

interface AppConfig {
  name: string;
  version: string;
  logo: string;
  theme: string;
  features: string[];
}

export interface FrontendConfig {
  navigation_items: NavigationItem[];
  app_config: AppConfig;
}

interface UseFrontendConfigReturn {
  navigationItems: NavigationItem[];
  appConfig: AppConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

let warmedFrontendConfig: FrontendConfig | undefined;
let warmedFrontendConfigTenantId = "";
/** `full` wins over shell refetch so HRM L3 cold deep-links keep expanded children. */
let frontendConfigNavDepth: "shell" | "full" = "shell";

/** Warm cache from SecurityContext bootstrap (safe before/while QueryProvider is mounted). */
export function applyWarmedFrontendConfig(config: FrontendConfig): void {
  warmedFrontendConfig = config;
  warmedFrontendConfigTenantId = readActiveTenantQueryScope();
}

export function seedFrontendConfigCache(config: FrontendConfig): void {
  const tenantId = readActiveTenantQueryScope();
  if (tenantId.length === 0) {
    warmedFrontendConfig = config;
    warmedFrontendConfigTenantId = "";
    return;
  }
  applyWarmedFrontendConfig(config);
  getPlatformQueryClient().setQueryData(tenantScopedQueryKey(tenantId, FRONTEND_CONFIG_QUERY_KEY), config);
}

/** Drop nav/config cache on logout / failed hydrate so the next login cannot reuse a blank shell. */
export function clearFrontendConfigCache(): void {
  warmedFrontendConfig = undefined;
  warmedFrontendConfigTenantId = "";
  frontendConfigNavDepth = "shell";
  getPlatformQueryClient().removeQueries({
    predicate: (query) => query.queryKey.includes(FRONTEND_CONFIG_QUERY_KEY[0]),
  });
}

export async function fetchFrontendConfig(): Promise<FrontendConfig> {
  const tenantId = readActiveTenantQueryScope();
  if (tenantId.length > 0 && frontendConfigNavDepth === "full") {
    const existing = getPlatformQueryClient().getQueryData<FrontendConfig>(
      tenantScopedQueryKey(tenantId, FRONTEND_CONFIG_QUERY_KEY),
    );
    if (existing !== undefined && Array.isArray(existing.navigation_items)) {
      return existing;
    }
  }
  const response = await apiService.getFrontendConfig({ view: "shell" });
  if (!response.success || response.data === undefined || response.data === null) {
    throw new Error(gatewayResponseErrorMessage(response));
  }
  const config = response.data as FrontendConfig;
  if (!Array.isArray(config.navigation_items)) {
    throw new Error("Frontend configuration missing navigation_items");
  }
  if (frontendConfigNavDepth !== "full") {
    frontendConfigNavDepth = "shell";
  }
  return config;
}

/** Expand full navigation (e.g. HRM children) on demand — not part of cold bootstrap. */
export async function fetchFrontendConfigFull(): Promise<FrontendConfig> {
  const tenantId = readActiveTenantQueryScope();
  if (tenantId.length === 0) {
    throw new Error("Frontend full config requires active tenant scope");
  }
  const response = await apiService.getFrontendConfig();
  if (!response.success || response.data === undefined || response.data === null) {
    throw new Error(gatewayResponseErrorMessage(response));
  }
  const config = response.data as FrontendConfig;
  if (!Array.isArray(config.navigation_items)) {
    throw new Error("Frontend configuration missing navigation_items");
  }
  frontendConfigNavDepth = "full";
  seedFrontendConfigCache(config);
  return config;
}

function readSeededFrontendConfig(tenantId: string): FrontendConfig | undefined {
  const scopedKey = tenantScopedQueryKey(tenantId, FRONTEND_CONFIG_QUERY_KEY);
  const fromQuery = getPlatformQueryClient().getQueryData<FrontendConfig>(scopedKey);
  if (fromQuery !== undefined) {
    return fromQuery;
  }
  if (warmedFrontendConfigTenantId === tenantId) {
    return warmedFrontendConfig;
  }
  return undefined;
}

export const useFrontendConfig = (): UseFrontendConfigReturn => {
  const tenantId = useActiveTenantQueryScope();
  const seeded = readSeededFrontendConfig(tenantId);
  const query = useQuery({
    queryKey: tenantScopedQueryKey(tenantId, FRONTEND_CONFIG_QUERY_KEY),
    enabled: tenantId.length > 0,
    queryFn: fetchFrontendConfig,
    ...PLATFORM_QUERY_DEFAULTS,
    staleTime: FRONTEND_CONFIG_STALE_MS,
    retry: false,
    initialData: seeded,
    initialDataUpdatedAt: seeded !== undefined ? Date.now() : undefined,
  });

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query.refetch]);

  const errorMessage =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? String(query.error)
        : null;

  return {
    navigationItems: query.data?.navigation_items ?? [],
    appConfig: query.data?.app_config ?? null,
    loading: query.data === undefined && query.isPending,
    error: errorMessage,
    refetch,
  };
};
